import type { TLShapeId } from '@digitalsamba/tldraw';

import {
  ContentBoardPanelTool,
  ContentBoardTool,
  ContentBoardAvailablePanelTool,
  ContentBoardColor,
  ContentBoardFill,
} from 'features/content-whiteboard/types';

export const CONTENT_BOARD_PANEL_ICONS_MAP: Record<ContentBoardPanelTool, string> = {
  laser: 'laser-pointer-tool',
  pen: 'pen-tool',
  highlight: 'marker',
  text: 'text-box-tool',
  note: 'sticky-note',
  ellipse: 'circle-tool',
  rectangle: 'rectangle-tool',
  line: 'line-tool',
  arrow: 'arrow-tool',
  eraser: 'eraser-tool',
  cursor: 'cursor-arrow',
  undo: 'undo-arrow',
  redo: 'redo-arrow',
  clear: 'trash-delete',
};

export const CONTENT_BOARD_AVAILABLE_TOOLS_MAP: Record<
  ContentBoardAvailablePanelTool,
  ContentBoardTool
> = {
  laser: 'laser',
  pen: 'draw',
  highlight: 'highlight',
  text: 'text',
  note: 'note',
  ellipse: 'geo',
  rectangle: 'geo',
  line: 'line',
  arrow: 'arrow',
  eraser: 'eraser',
  cursor: 'select',
};

export const CONTENT_BOARD_TOOLS_MAP: Record<
  ContentBoardTool,
  ContentBoardAvailablePanelTool | undefined
> = {
  laser: 'laser',
  draw: 'pen',
  highlight: 'highlight',
  text: 'text',
  note: 'note',
  geo: 'rectangle',
  line: 'line',
  arrow: 'arrow',
  eraser: 'eraser',
  select: 'cursor',
};

export const CONTENT_BOARD_COLORS: ContentBoardColor[] = [
  'black',
  'grey',
  'light-violet',
  'violet',
  'light-green',
  'blue',
  'light-blue',
  'yellow',
  'orange',
  'red',
];

export const CONTENT_BOARD_COLORS_MAP: Record<ContentBoardColor, string> = {
  red: '#e03131',
  black: '#1D1D1D',
  blue: '#4963E3',
  green: '#41906B',
  grey: '#AFB5BC',
  orange: '#E6712D',
  violet: '#A147C3',
  yellow: '#F6C383',
  'light-violet': '#e599f7',
  'light-red': '#FF8787',
  'light-blue': '#66A9F1',
  'light-green': '#67BD64',
};

export const CONTENT_BOARD_FILLS: ContentBoardFill[] = ['none', 'semi', 'solid', 'pattern'];

export const CONTENT_BOARD_FILL_ICONS_MAP: Record<ContentBoardFill, string> = {
  none: 'fill-none',
  semi: 'fill-semi',
  solid: 'fill-solid',
  pattern: 'fill-pattern',
};

/** Tools whose shapes carry a `fill` prop — the rest render as strokes only. */
export const CONTENT_BOARD_FILL_TOOLS: ContentBoardAvailablePanelTool[] = [
  'pen',
  'ellipse',
  'rectangle',
];

export const CONTENT_BOARD_FILL_SHAPE_TYPES = ['draw', 'geo'];

export const CONTENT_BOARD_ERASER_COLOR = 'rgba(0,0,0,0.2)';

export const CONTENT_BOARD_BG_SHAPE_ID = 'shape:CONTENT_BOARD_BACKGROUND_IMAGE' as TLShapeId;

export const CONTENT_BOARD_SUPPORTED_FILE_TYPES = ['doc', 'ppt', 'xls', 'pdf', 'image'];

/**
 * Fixed width for the screenshare whiteboard coordinate space.
 * All users normalize their background bounds to this width (with proportional height),
 * so drawing coordinates are consistent regardless of the actual video resolution
 * each user receives (which can differ due to simulcast layers or WebRTC adaptation).
 */
export const SCREENSHARE_WHITEBOARD_NORMALIZED_WIDTH = 1920;
